import Box from '@mui/material/Box';
import { cloneDeep, isEqual } from 'lodash-es';
import { Fragment, type JSX, useState } from 'react';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { Text } from 'Components/Text/Text';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { countryOptions, getStateFieldLabel, getStateFieldOptions } from 'Config/CountryStateList';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { TPRM_THIRD_PARTY_CONTACT_EMAIL, TPRM_THIRD_PARTY_CONTACT_NAME, TPRM_THIRD_PARTY_CONTACT_PHONE_NUMBER, TPRM_THIRD_PARTY_EIN, TPRM_THIRD_PARTY_MANAGER, TPRM_THIRD_PARTY_NAME } from 'Config/Tooltips';
import { validateEin, validateEmail } from 'Helpers/InputValidation';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { ValidationError } from 'Models/ErrorTypes';
import { FinalizationState } from 'Models/Organization';
import { CreateThirdPartyRequest, ThirdPartyContact, ThirdPartyForNonTprmUser, ThirdPartyResponse, UpdateThirdPartyRequest } from 'Models/TPRM';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import styles from './ManageThirdPartyFormFields.module.css';
import { DeleteThirdPartyModal } from '../DeleteThirdPartyModal/DeleteThirdPartyModal';

export interface ManageThirdPartyFormFieldsProps {
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    onFormSubmitBegin: () => void;
    onFormSubmitFailed: (errorMessage: string) => void;
    onFormSubmittedSuccessfully: (thirdParty: ThirdPartyResponse | ThirdPartyForNonTprmUser) => Promise<void>;
    tprmApi: ClientTPRMApi;
    userHasFullTprmAccess: boolean;

    /** If `true`, the user will not be able to delete the third-party service. At the time of writing, this is `true` only for the TPRM Intake page. */
    hideDeleteButton?: boolean;
    /** If present, the text field for entering the third party's name will not be rendered by this component, and it will be the parent's job to manage the UI and state for the name. At the time of writing, this is the case only for the TPRM Intake page, where the user must first choose whether they are editing a new or existing third party. */
    name?: string;
    thirdParty?: ThirdPartyResponse | ThirdPartyForNonTprmUser;
}

interface FormFieldsState {
    country?: string;
    thirdPartyManagerUserId?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    ein?: string;
    state?: string;
    tags: string[];
    thirdPartyContacts: ThirdPartyContact[];
    website?: string;
    zipCode?: string;
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft';
type RequestActionConfirmationNeeded = 'delete';

type RequestAndModalState =
    | {
          requestState: 'idle';
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'failure';
          deleteConfirmationModalIsActive: false;
      };

/**
 * Renders a form for creating a new third party, or updating or deleting an existing third party.
 * Used on the Manage Third Party page as well as on the TPRM Intake page.
 */
export const ManageThirdPartyFormFields = (props: ManageThirdPartyFormFieldsProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle', deleteConfirmationModalIsActive: false });
    const [thirdPartyManager, setThirdPartyManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.thirdParty?.vendor_manager_user_id)); // Keeps track of the currently selected User in the Third-Party Manager drop-down menu.

    // Used only if the parent is not managing the name. Do not use this state directly; use `name`.
    const [managedName, setManagedName] = useState<string | undefined>(props.thirdParty?.name);
    const name = props.name ?? managedName;

    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        country: props.thirdParty?.country,
        thirdPartyManagerUserId: props.thirdParty?.vendor_manager_user_id,
        addressLine1: props.thirdParty?.address_line_1,
        addressLine2: props.thirdParty?.address_line_2,
        city: props.thirdParty?.city,
        ein: props.thirdParty?.ein,
        state: props.thirdParty?.state,
        tags: props.thirdParty?.tags ?? [],
        thirdPartyContacts: props.thirdParty?.vendor_contacts ?? [],
        website: props.thirdParty?.website,
        zipCode: props.thirdParty?.zip_code,
    });

    const [hasUnsavedChanges, onFormFieldValuesSaved] = useUnsavedChangesTracking(formFieldsState, false);

    const createThirdParty = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'createAsDraft', deleteConfirmationModalIsActive: false });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const thirdParty = (await props.tprmApi.createThirdParty(buildRequestForNewThirdParty())).data;
            await props.onFormSubmittedSuccessfully(thirdParty);
            setRequestAndModalState({ requestState: 'success', completedAction: 'createAsDraft', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateThirdParty = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit', deleteConfirmationModalIsActive: false });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const thirdParty = (await props.tprmApi.updateThirdParty(buildRequestForExistingThirdParty(), props.thirdParty!.id)).data;
            await props.onFormSubmittedSuccessfully(thirdParty);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    /** Build the request object for creating a new Third Party. This ensures only necessary fields are included in the request to the API. */
    const buildRequestForNewThirdParty = (): CreateThirdPartyRequest => {
        const request: CreateThirdPartyRequest = {
            finalization_state: FinalizationState.DRAFT_IN_PROGRESS,
            country: formFieldsState.country!,
            name: name!,
            vendor_manager_user_id: formFieldsState.thirdPartyManagerUserId!,
            tags: formFieldsState.tags,
            vendor_contacts: cloneDeep(formFieldsState.thirdPartyContacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        if (formFieldsState.addressLine1) {
            request.address_line_1 = formFieldsState.addressLine1;
        }
        if (formFieldsState.addressLine2) {
            request.address_line_2 = formFieldsState.addressLine2;
        }
        if (formFieldsState.city) {
            request.city = formFieldsState.city;
        }
        if (formFieldsState.ein) {
            request.ein = formFieldsState.ein;
        }
        if (formFieldsState.state) {
            request.state = formFieldsState.state;
        }
        if (formFieldsState.website) {
            request.website = formFieldsState.website;
        }
        if (formFieldsState.zipCode) {
            request.zip_code = formFieldsState.zipCode;
        }

        let third_party_contacts = request.vendor_contacts;
        third_party_contacts = filterEmptyThirdPartyContacts(third_party_contacts);
        third_party_contacts = convertEmptyThirdPartyContactAttributesToUndefined(third_party_contacts);
        request.vendor_contacts = third_party_contacts;

        return request;
    };

    // TODO: Remove `Undefined` from the backend and build this like any other request in the app.
    /** Build the request object for updating an existing Third Party. This ensures only necessary fields are included in the request to the API. */
    const buildRequestForExistingThirdParty = (): UpdateThirdPartyRequest => {
        const request: UpdateThirdPartyRequest = {};
        if (props.thirdParty!.country !== formFieldsState.country) {
            request.country = formFieldsState.country;
        }
        if (props.thirdParty!.name !== name) {
            request.name = name;
        }
        if (props.thirdParty!.vendor_manager_user_id !== formFieldsState.thirdPartyManagerUserId) {
            request.vendor_manager_user_id = formFieldsState.thirdPartyManagerUserId;
        }
        if (props.thirdParty!.address_line_1 !== formFieldsState.addressLine1) {
            request.address_line_1 = formFieldsState.addressLine1;
        }
        if (props.thirdParty!.address_line_2 !== formFieldsState.addressLine2) {
            request.address_line_2 = formFieldsState.addressLine2;
        }
        if (props.thirdParty!.city !== formFieldsState.city) {
            request.city = formFieldsState.city;
        }
        if (props.thirdParty!.ein !== formFieldsState.ein) {
            request.ein = formFieldsState.ein;
        }
        if (props.thirdParty!.state !== formFieldsState.state) {
            request.state = formFieldsState.state;
        }
        if (props.thirdParty!.tags !== formFieldsState.tags) {
            request.tags = formFieldsState.tags;
        }
        if (!isEqual(props.thirdParty!.vendor_contacts.sort(), formFieldsState.thirdPartyContacts?.sort())) {
            request.vendor_contacts = convertEmptyThirdPartyContactAttributesToUndefined(filterEmptyThirdPartyContacts(cloneDeep(formFieldsState.thirdPartyContacts))); // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        }
        if (props.thirdParty!.website !== formFieldsState.website) {
            request.website = formFieldsState.website;
        }
        if (props.thirdParty!.zip_code !== formFieldsState.zipCode) {
            request.zip_code = formFieldsState.zipCode;
        }
        return request;
    };

    /** Discard any Third-Party contacts where all attributes are "empty." */
    const filterEmptyThirdPartyContacts = (thirdPartyContacts: ThirdPartyContact[]): ThirdPartyContact[] => {
        return thirdPartyContacts.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });
    };

    /**
     * Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
     * This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
     */
    const convertEmptyThirdPartyContactAttributesToUndefined = (thirdPartyContacts: ThirdPartyContact[]): ThirdPartyContact[] => {
        thirdPartyContacts.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });
        return thirdPartyContacts;
    };

    // TODO [TPRM - managing third parties and services]: Define a reusable component for managing third-party contacts. Include logic and tests for things like validating email addresses and discarding empty contacts before saving.
    /** Perform input validation. */
    const validateForm = (): void => {
        // Validate required attributes.
        if (!name) {
            throw new ValidationError('Name is required.');
        }
        if (!formFieldsState.thirdPartyManagerUserId) {
            throw new ValidationError('Manager is required.');
        }
        if (!formFieldsState.country) {
            throw new ValidationError('Country/Region is required.');
        }

        // Validate optional attributes.
        if (formFieldsState.ein && !validateEin(formFieldsState.ein)) {
            throw new ValidationError('Employer ID Number must only contain numbers and dashes.');
        }

        formFieldsState.thirdPartyContacts.forEach((thirdPartyContact) => {
            if (thirdPartyContact.email_address && !validateEmail(thirdPartyContact.email_address)) {
                throw new ValidationError('Invalid email address.');
            }
        });
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        props.onFormSubmitFailed(error.message);
        setRequestAndModalState({ requestState: 'failure', deleteConfirmationModalIsActive: false });
    };

    /** Handles changes made to general text fields. */
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    /** Handles changes made to general drop-down (select) fields. */
    const handleSelectChange = (value: ChangeEventType, formFieldId: string): void => {
        if (formFieldId === 'country') {
            setFormFieldsState({ ...formFieldsState, country: value as string | undefined, state: '' });
        } else {
            setFormFieldsState({ ...formFieldsState, [formFieldId]: value });
        }
    };

    /** Handles changes made to the Third-Party Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user !== thirdPartyManager) {
            if (user === undefined) {
                setThirdPartyManager(undefined);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
            } else {
                setThirdPartyManager(user);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
            }
        }
    };

    /** Adds the fields (empty) for a new Third-Party contact. */
    const handleAddThirdPartyContact = (): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts.push({ name: '', email_address: '', phone_number: '', additional_information: '' });
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Deletes an existing Third-Party contact. */
    const handleDeleteThirdPartyContact = (index: number): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts.splice(index, 1);
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Handles changes made to an existing Third-Party contact. */
    const handleThirdPartyContactChange = (index: number, attribute: 'name' | 'email_address' | 'phone_number' | 'additional_information', value: string): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts[index] = {
            ...thirdPartyContacts[index],
            [attribute]: value,
        };
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Renders a field for selecting the state, region, territory, province, etc. of the selected country. */
    const renderStateField = (): JSX.Element => {
        const formFieldLabel = getStateFieldLabel(formFieldsState.country);

        if (!formFieldsState.country) {
            // If a country has not yet been selected, return a disabled text field.
            return <FormFieldText value="Select a Country first" disabled formFieldType="text" formFieldId="state" formFieldLabel={formFieldLabel} required={false} />;
        }

        const stateFieldOptions = getStateFieldOptions(formFieldsState.country);

        if (stateFieldOptions === undefined) {
            // If the selected country does not have corresponding states/etc. from which to select, return a free-form text field.
            // The value defaults to the empty string to avoid transitioning the component from uncontrolled to controlled.
            return <FormFieldText value={formFieldsState.state || ''} formFieldType="text" handleChange={handleChange} formFieldId="state" formFieldLabel={formFieldLabel} required={false} />;
        }

        // If the selected country has corresponding states/etc. from which to select, return a select component with those values.
        // The value defaults to null to avoid transitioning the component from uncontrolled to controlled, and to clear out the selection when a new country is selected.
        return <FormFieldSelect selectedOption={formFieldsState.state || null} options={stateFieldOptions} handleChange={handleSelectChange} formFieldId="state" formFieldLabel={formFieldLabel} disabled={formDisabled} />;
    };

    const thirdParty = props.thirdParty;
    const formDisabled = requestAndModalState.requestState === 'inProgress' || (requestAndModalState.requestState === 'success' && requestAndModalState.completedAction === 'delete');

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.deleteConfirmationModalIsActive && thirdParty && <DeleteThirdPartyModal exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} onSuccess={async () => setRequestAndModalState({ requestState: 'success', completedAction: 'delete', deleteConfirmationModalIsActive: true })} onHideModal={() => setRequestAndModalState({ ...requestAndModalState, deleteConfirmationModalIsActive: false })} thirdParty={props.thirdParty!} tprmApi={props.tprmApi} userHasFullTprmAccess={props.userHasFullTprmAccess} />}
            <Box component="form" noValidate>
                <fieldset disabled={formDisabled}>
                    {!props.name && (
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={name || ''} formFieldType="text" handleChange={(event: React.FormEvent<HTMLInputElement>) => setManagedName(event.currentTarget.value)} formFieldId="thirdPartyName" formFieldLabel="Legal Name" required tooltip={TPRM_THIRD_PARTY_NAME} />
                        </div>
                    )}
                    <div className={styles.formFieldContainer}>
                        <FormFieldText value={formFieldsState.website || ''} formFieldType="text" handleChange={handleChange} formFieldId="website" formFieldLabel="Website URL" required={false} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserSelect rolesRestriction={TPRM_ROLES} selectedUser={thirdPartyManager} onUserSelected={handleSelectUserChange} formFieldId="thirdPartyManagerUserId" formFieldLabel="Manager" tooltip={TPRM_THIRD_PARTY_MANAGER} required disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldSelect selectedOption={formFieldsState.country} options={countryOptions} handleChange={handleSelectChange} formFieldId="country" formFieldLabel="Country/Region" required disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldGroup}>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={formFieldsState.addressLine1 || ''} formFieldType="text" handleChange={handleChange} formFieldId="addressLine1" formFieldLabel="Address Line 1" required={false} />
                        </div>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={formFieldsState.addressLine2 || ''} formFieldType="text" handleChange={handleChange} formFieldId="addressLine2" formFieldLabel="Address Line 2" required={false} />
                        </div>
                    </div>
                    <div className={styles.formFieldGroup}>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={formFieldsState.city || ''} formFieldType="text" handleChange={handleChange} formFieldId="city" formFieldLabel="City" required={false} />
                        </div>
                        <div className={styles.formFieldContainer}>{renderStateField()}</div>
                    </div>
                    <div className={styles.formFieldGroup}>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={formFieldsState.zipCode || ''} formFieldType="text" handleChange={handleChange} formFieldId="zipCode" formFieldLabel="Zip Code" required={false} />
                        </div>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText value={formFieldsState.ein || ''} formFieldType="text" handleChange={handleChange} formFieldId="ein" formFieldLabel="Employer ID Number" tooltip={TPRM_THIRD_PARTY_EIN} />
                        </div>
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldMultiOptionSelect
                            defaultSelectedOptions={cachedData
                                .getTagCategoriesGroupedOptions()
                                .map((group) => group.options)
                                .flat()
                                .filter((option) => formFieldsState.tags.includes(option.value as string))}
                            formFieldLabel="Tags"
                            formFieldId="tags"
                            handleChange={(value: GroupOptionType[]) => setFormFieldsState((current) => ({ ...current, tags: value.map((tag) => tag.value as string) }))}
                            options={cachedData.getTagCategoriesGroupedOptions()}
                            accessibilityLabel="Tags"
                            disabled={formDisabled}
                        />
                    </div>
                    <Text variant="Header3" color="darkGray">
                        Third-Party Contact Information
                    </Text>
                    {formFieldsState.thirdPartyContacts.map((thirdPartyContact, index) => (
                        <Fragment key={index}>
                            <div className={styles.formFieldGroup}>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'name', event.currentTarget.value)} formFieldId={`thirdPartyContactName${index}`} formFieldLabel={`Name`} tooltip={TPRM_THIRD_PARTY_CONTACT_NAME} value={thirdPartyContact.name || ''} />
                                </div>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="email" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'email_address', event.currentTarget.value)} formFieldId={`thirdPartyContactEmailAddress${index}`} formFieldLabel={`Email Address`} tooltip={TPRM_THIRD_PARTY_CONTACT_EMAIL} value={thirdPartyContact.email_address || ''} />
                                </div>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'phone_number', event.currentTarget.value)} formFieldId={`thirdPartyContactPhoneNumber${index}`} formFieldLabel={`Phone Number`} tooltip={TPRM_THIRD_PARTY_CONTACT_PHONE_NUMBER} value={thirdPartyContact.phone_number || ''} />
                                </div>
                                <div className={styles.trashIconContainer}>
                                    <IconButton aria-label={`delete third-party contact ${index}`} onClick={() => handleDeleteThirdPartyContact(index)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                </div>
                            </div>
                            <div className={styles.formFieldContainer}>
                                <FormFieldTextArea handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'additional_information', event.currentTarget.value)} formFieldId={`thirdPartyContactAdditionalInformation${index}`} formFieldLabel={`Additional Information`} tooltip="Any additional information for the contact at this third party." value={thirdPartyContact.additional_information || ''} />
                            </div>
                        </Fragment>
                    ))}
                    <div className={styles.formFieldContainerBottom}>
                        <Button variant="linkText" size="lg" onClick={handleAddThirdPartyContact}>
                            {'+ Add Contact'}
                        </Button>
                    </div>
                    <div className={'modalFormButtonContainer'}>
                        {props.thirdParty && !props.hideDeleteButton && (
                            <div className={styles.deleteButton}>
                                <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', deleteConfirmationModalIsActive: true })} variant="danger" isLoading={false} loadingText="Deleting...">
                                    Delete
                                </Button>
                            </div>
                        )}
                        <Button
                            variant="primary"
                            fontAwesomeImage={ICON_SAVE}
                            isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === (props.thirdParty ? 'edit' : 'createAsDraft')}
                            loadingText="Saving..."
                            onClick={() => {
                                if (props.thirdParty) {
                                    updateThirdParty();
                                } else {
                                    createThirdParty();
                                }
                            }}
                        >
                            Save
                        </Button>
                    </div>
                    <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                </fieldset>
            </Box>
        </>
    );
};
